// The languages the Studio speaks and how a page, the room and the server
// pick one: no words here, so a page's small script can carry it without the
// catalogs (src/core/i18n.ts has those and re-exports all of this).
//
// How a page knows its language, in this order:
//   1. `?lang=` in its address (the page saves it, then drops it from the address);
//   2. a `/<lang>/` prefix on a public page of the Studio's host (`/ja/terms/`);
//   3. the saved choice: the cookie LANG_COOKIE on the Studio's host, a game
//      host's own localStorage there (no cookie crosses hosts);
//   4. the browser's first language.

export type Lang = "en" | "ja";
export const LANGS: readonly Lang[] = ["en", "ja"];
export const DEFAULT_LANG: Lang = "en";

/** The cookie on the Studio's host that holds a visitor's chosen language. */
export const LANG_COOKIE = "lang";
/** The address parameter that names a language, on a link between pages. */
export const LANG_PARAM = "lang";
/** A year: how long a chosen language is kept. */
export const LANG_COOKIE_SECONDS = 365 * 24 * 60 * 60;

export interface LangInfo {
  /** The language's own name for itself, as a switch shows it. */
  name: string;
  /** BCP 47 tag for `lang` attributes and Intl. */
  tag: string;
  /** Open Graph locale. */
  ogLocale: string;
  /** The locale the payment page and the billing portal open in (the payment provider's code for it). */
  checkout: string;
}

export const LANG_INFO: Record<Lang, LangInfo> = {
  en: { name: "English", tag: "en", ogLocale: "en_US", checkout: "en" },
  ja: { name: "日本語", tag: "ja", ogLocale: "ja_JP", checkout: "ja" },
};

/** `value` as a language, or null when it names none of LANGS. Case and region are ignored: `ja-JP` is `ja`. */
export function parseLang(value: unknown): Lang | null {
  if (typeof value !== "string") return null;
  const base = value.trim().toLowerCase().split(/[-_]/)[0];
  return LANGS.find((lang) => lang === base) ?? null;
}

/** The language a path's first segment names (`/ja/terms/` is ja), or null for an unprefixed path. English has no prefix. */
export function langFromPath(path: string): Lang | null {
  const first = /^\/([a-z]{2})(?:\/|$)/.exec(path)?.[1];
  const lang = parseLang(first);
  return lang && lang !== DEFAULT_LANG ? lang : null;
}

/** The language of a browser's list of languages: its first entry, when it is one of LANGS; English otherwise. */
export function langFromLanguages(languages: readonly string[] | undefined): Lang {
  return parseLang(languages?.[0]) ?? DEFAULT_LANG;
}

export interface LangSources {
  /** The address's `?lang=`. */
  query?: string | null;
  /** The address's path, for a public page with a language prefix. */
  path?: string | null;
  /** The saved choice: the cookie, or a game host's storage. */
  saved?: string | null;
  /** The browser's languages (`navigator.languages`, or an Accept-Language header read in order). */
  languages?: readonly string[];
}

/** The language for a page, by the order at the top of this file. */
export function resolveLang(sources: LangSources): Lang {
  return parseLang(sources.query) ?? (sources.path ? langFromPath(sources.path) : null) ?? parseLang(sources.saved) ?? langFromLanguages(sources.languages);
}

/** `path` in `lang`: English keeps it, another language gets its prefix. `/terms/` in ja is `/ja/terms/`. */
export function localePath(lang: Lang, path: string): string {
  const bare = unprefixed(path);
  return lang === DEFAULT_LANG ? bare : `/${lang}${bare === "/" ? "/" : bare}`;
}

/** `path` without a language prefix: `/ja/terms/` is `/terms/`, `/ja` is `/`. */
export function unprefixed(path: string): string {
  const lang = langFromPath(path);
  if (!lang) return path || "/";
  const rest = path.slice(lang.length + 1);
  return rest === "" ? "/" : rest;
}

/** The languages of an Accept-Language header, best first. */
export function acceptLanguages(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(";");
      const q = params.map((param) => /^\s*q=([0-9.]+)/.exec(param)?.[1]).find(Boolean);
      return { tag: tag!.trim(), q: q === undefined ? 1 : Number(q), index };
    })
    .filter((entry) => entry.tag && entry.tag !== "*" && entry.q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map((entry) => entry.tag);
}

/** The value of cookie `name` in a Cookie header, or null. */
export function cookieValue(header: string | null | undefined, name: string): string | null {
  for (const part of (header ?? "").split(";")) {
    const at = part.indexOf("=");
    if (at > 0 && part.slice(0, at).trim() === name) return decodeURIComponent(part.slice(at + 1).trim());
  }
  return null;
}

/**
 * The language of a request to the Studio's host: `?lang=`, a path prefix,
 * the saved cookie, then the browser's Accept-Language. The server answers
 * mail, refusals the room shows and the games' pages in it.
 */
export function requestLang(request: { url?: string; headers: { get(name: string): string | null } }): Lang {
  const url = request.url ? new URL(request.url) : null;
  return resolveLang({
    query: url?.searchParams.get(LANG_PARAM),
    path: url?.pathname,
    // `x-pocket-lang` is the room's own word for the language it shows; the cookie is the saved choice,
    // read under its plain name (server/cookies.ts removes the `__Host-` prefix before the API reads
    // cookies) and under the prefixed one (a request read before that).
    saved: request.headers.get("x-pocket-lang") ?? cookieValue(request.headers.get("cookie"), LANG_COOKIE) ?? cookieValue(request.headers.get("cookie"), `__Host-${LANG_COOKIE}`),
    languages: acceptLanguages(request.headers.get("accept-language")),
  });
}

